import {
  activeSubscriptionSelector,
  loggedInSelector,
} from 'spa/selectors/users'
import {
  addSongToQueue as addSongToQueueAction,
  removeSongFromQueue as removeSongFromQueueAction,
} from 'spa/action_creators/player'
import { Badge } from '../badge/badge'
import { Carousel } from '../../components/carousel/carousel'
import { CarouselNavControls } from '../carousel/CarouselNavControls'
import { clearSong, fetchSongDetails } from 'spa/action_creators/song_details'
import {
  DEFAULT_KEY_INFORMATION,
  DEFAULT_SHORT_DATE_OPTION,
  getQueryParams,
} from 'spa/shared/utils'
import { defineMessages, FormattedMessage, injectIntl } from 'react-intl'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Helmet } from 'react-helmet'
import { isMobileSelector } from '../../selectors/device'
import { Link } from 'spa/components/link'
import { linkifyArtistNames } from 'spa/urls'
import { Loading } from 'spa/components/loading'
import { makeSongUrl } from 'spa/urls'
import { PlayTrackButton } from 'spa/components/track_buttons/play_track_button'
import { Popover } from '../popover'
import { PreviewQueueButton } from 'spa/components/preview_queue_button'
import { queuedSongsSelector } from '../../selectors/songs'
import { Redirect, useLocation } from 'react-router-dom'
import { Sidebar } from 'spa/components/layout'
import { SimpleSongsList2 } from '../simple_songs_list_2'
import { TextStyle, TextStyleMultiline } from 'spa/components/typography'
import { TrackDropDownTableInfo } from '../track_dropdown_table'
import { TRACKLIST } from '../../shared/constants'
import { TrackList, TrackListItem } from 'spa/components/track_list'
import { TrackListItemAll } from '../track_list/track_list_item_all'
import { useDispatch, useSelector } from 'react-redux'
import classNames from 'classnames'
import PropTypes from 'prop-types'
import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import styles from './styles'

const localizeMessages = defineMessages({
  beatsourceLink: {
    id: 'djcity.records.beatsource.text.link',
    defaultMessage: 'Search <%=artist%> on Beatsource',
  },
  beatsourceSearchLink: {
    id: 'djcity.records.beatsource.search.link',
    defaultMessage: 'https://www.beatsource.com/search',
  },
  bpm: {
    id: 'djcity.records.recordpool.track.bpm',
    defaultMessage: 'BPM',
  },
  key: {
    id: 'djcity.records.recordpool.track.key',
    defaultMessage: 'Key',
  },
  genre: {
    id: 'djcity.records.recordpool.track.genre',
    defaultMessage: 'Genre',
  },
  releaseDate: {
    id: 'djcity.records.recordpool.track.release',
    defaultMessage: 'Release Date',
  },
  metaDescription: {
    id: 'djcity.records.meta.description',
    defaultMessage:
      '<%=artist%> - <%=title%> | DJcity. Get all the tracks you need with access to 150+ genres, optimized for the top DJ software. DJcity is the leading music discovery & delivery platform for DJs around the world.',
  },
})

export function SongDetails({ intl }) {
  const dispatch = useDispatch()
  const isFetching = useSelector(state => state.songDetails.isFetching)
  const isMobile = useSelector(isMobileSelector)
  const isActive = useSelector(activeSubscriptionSelector)
  const song = useSelector(state => state.songDetails.song)
  const queuedSongIds = useSelector(queuedSongsSelector).map(song => song.rid)
  const songsById = useSelector(state => state.songs.songsById)
  const isLoggedIn = useSelector(loggedInSelector)
  const location = useLocation()
  const stringId = location.pathname.replace(/(\/records\/)|(\/record\/)/, '')
  const id = parseInt(stringId.split('-').shift(), 10)
  const songUrl = song ? makeSongUrl(song) : ''
  const carouselRef = useRef(null)
  const [carouselOverflow, setCarouselOverflow] = useState({
    left: false,
    right: false,
  })

  const checkOverflow = direction => {
    return direction === 'left'
      ? !carouselOverflow.left
      : !carouselOverflow.right
  }

  const handleClick = direction => {
    if (carouselRef.current) {
      carouselRef.current.handleCarouselScroll(direction)
    }
  }

  useLayoutEffect(() => {
    if (location.hash) {
      const el = document.querySelector(location.hash)
      if (el) {
        requestAnimationFrame(() => {
          window.scrollTo({
            behavior: 'smooth',
            top:
              el.getBoundingClientRect().top +
              window.pageYOffset -
              (isLoggedIn && !isActive && !isMobile ? 160 : 120),
          })
        })
      }
    }
  }, [location, song])

  // Clears the current song data when components mounts
  // and when user logs in/out
  useEffect(() => {
    dispatch(clearSong())
    return () => dispatch(clearSong())
  }, [dispatch, isLoggedIn])

  // Fetches the song data if there's no song or if the current loaded song
  // doesn't match the song id in the URL
  useEffect(() => {
    if (!isFetching && (!song || parseInt(song.rid, 10) !== parseInt(id))) {
      dispatch(fetchSongDetails({ id }))
    }
  }, [dispatch, id, isFetching, song])

  const addSongToQueue = useCallback(
    (song, trackList) => dispatch(addSongToQueueAction(song, trackList)),
    [dispatch]
  )

  const removeSongFromQueue = useCallback(
    song => {
      dispatch(removeSongFromQueueAction(song))
    },
    [dispatch]
  )

  const onClickQueue = useCallback(() => {
    if (!song) {
      return
    }
    if (queuedSongIds.includes(parseInt(song.rid, 10))) {
      removeSongFromQueue(song)
    } else {
      addSongToQueue(song)
    }
  }, [dispatch, queuedSongIds, song])

  const currentlyInQueue =
    song && queuedSongIds.includes(parseInt(song.rid, 10))

  if (
    song &&
    id === parseInt(song.rid) &&
    songUrl &&
    songUrl !== `/records/${decodeURIComponent(stringId)}` // browsers do partial encoding to url
  ) {
    return <Redirect to={songUrl} />
  }

  return (
    <div
      className={classNames(styles.songDetailWrapper, {
        [styles.mobile]: isMobile,
      })}
    >
      {!song || isFetching ? (
        <Loading />
      ) : !isMobile ? (
        <>
          <div className={styles.songDetails}>
            <div className={styles.songDetailsFirstSection}>
              <div className={styles.firstRow}>
                <div className={styles.titleContainer}>
                  <PlayTrackButton
                    className={styles.playButton}
                    songId={song.rid}
                  />
                  <div>
                    <div className={styles.songTitle}>{song.title}</div>
                    <TextStyle
                      className={classNames('artist-names', styles.artistName)}
                      color="white"
                      uppercase
                      variant="extra-bold"
                    >
                      {linkifyArtistNames(song)}
                    </TextStyle>
                  </div>
                </div>
                {isLoggedIn && (
                  <PreviewQueueButton
                    className={styles.queueButton}
                    currentlyInQueue={currentlyInQueue}
                    onClick={onClickQueue}
                  />
                )}
              </div>
              <div className={styles.metadataLine}>
                {song.remixer ? (
                  <div className={styles.meta}>
                    <TextStyle color="white" variant="extra-bold">
                      <FormattedMessage
                        defaultMessage="Remixer"
                        id="djcity.records.recordpool.track.remixer"
                      />
                    </TextStyle>
                    <Link to={`/remixer/${song.remixer.slug}`}>
                      <TextStyle color="red" variant="label">
                        {song.remixer.name}
                      </TextStyle>
                    </Link>
                  </div>
                ) : null}
                <div className={styles.meta}>
                  <TextStyle color="white" variant="extra-bold">
                    {intl.formatMessage(localizeMessages.bpm)}
                  </TextStyle>
                  {song.bpm}
                </div>
                {song.key !== DEFAULT_KEY_INFORMATION ? (
                  <div className={styles.meta}>
                    <TextStyle color="white" variant="extra-bold">
                      {intl.formatMessage(localizeMessages.key)}
                    </TextStyle>
                    {song.key}
                  </div>
                ) : null}
                <div className={styles.meta}>
                  <TextStyle color="white" variant="extra-bold">
                    {intl.formatMessage(localizeMessages.genre)}
                  </TextStyle>
                  {song.category}
                </div>
                <div className={styles.meta}>
                  <TextStyle color="white" variant="extra-bold">
                    <FormattedMessage
                      defaultMessage="Release Date"
                      id="djcity.records.recordpool.track.release"
                    />
                  </TextStyle>
                  <span>
                    {intl.formatDate(
                      song.releasedate,
                      DEFAULT_SHORT_DATE_OPTION
                    )}
                  </span>
                </div>
                {song.recordLabel && song.recordLabel.labelName ? (
                  <div className={styles.meta}>
                    <div className={styles.additionalInfoBlock}>
                      <TextStyle color="white" variant="extra-bold">
                        <FormattedMessage
                          defaultMessage="Label"
                          id="djcity.records.recordpool.track.label"
                        />
                      </TextStyle>
                      <TextStyleMultiline
                        className={styles.additionalInfoBlockText}
                        color="white"
                        tagName="div"
                        variant="label"
                      >
                        {song.recordLabel.labelName}
                      </TextStyleMultiline>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            <div className={styles.sectionSeparator} />
            <div className={styles.songDetailsSecondSection}>
              <div className={styles.tagsAndOtherInfo}>
                <div className={styles.tagList}>
                  {song.tags &&
                    song.tags.map(({ id, name, slug }) => {
                      return (
                        <Link
                          className={styles.tag}
                          key={id}
                          to={`/genre/${slug}`}
                        >
                          {name}
                        </Link>
                      )
                    })}
                </div>
                <div className={styles.otherInfo}>
                  {song.otherInfo ? (
                    <div className={styles.additionalInfoBlock}>
                      <TextStyle color="white" variant="extra-bold">
                        <FormattedMessage
                          defaultMessage="Other Info"
                          id="djcity.records.recordpool.track.description"
                        />
                      </TextStyle>
                      <TextStyleMultiline
                        className={styles.additionalInfoBlockText}
                        color="white"
                        tagName="div"
                        variant="label"
                      >
                        {song.otherInfo}
                      </TextStyleMultiline>
                    </div>
                  ) : null}
                  <div className={styles.moreInfoBlock}>
                    <TextStyle color="white" variant="extra-bold">
                      <FormattedMessage
                        defaultMessage="More"
                        id="djcity.records.recordpool.track.more"
                      />
                    </TextStyle>
                    <a
                      className={styles.webLink}
                      href={`${intl.formatMessage(
                        localizeMessages.beatsourceSearchLink
                      )}?${getQueryParams({ q: song.artist })}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {intl.formatMessage(localizeMessages.beatsourceLink, {
                        artist: song.artist,
                      })}
                    </a>
                  </div>
                </div>
              </div>
              <div className={styles.versions}>
                <div className={styles.versionBlock}>
                  <Popover
                    closeOnScroll={false}
                    contentClassName={styles.versionPopoverContent}
                    renderTrigger={({ handleClick, ref }) => (
                      <button
                        className={classNames(
                          'cleanButton',
                          styles.versionButton
                        )}
                        onClick={handleClick}
                        ref={ref}
                      >
                        <FontAwesomeIcon
                          className={styles.infoVersion}
                          icon="info-circle"
                        />
                        <TextStyle color="white" variant="extra-bold">
                          <FormattedMessage
                            defaultMessage="Versions"
                            id="djcity.records.recordpool.track.versions"
                          />
                        </TextStyle>
                      </button>
                    )}
                    variant="light"
                  >
                    <TrackDropDownTableInfo />
                  </Popover>
                </div>
                <TrackList className="darkMode">
                  {song.types.map(track => {
                    return (
                      <TrackListItem
                        bold
                        className={styles.trackListItemDetails}
                        fileDate={track.fileDate}
                        key={track.tid}
                        songId={song?.rid}
                        trackId={track.tid}
                      />
                    )
                  })}
                  <TrackListItemAll
                    bold
                    className={[
                      styles.trackListItemDetails,
                      styles.trackListItemDetailsAll,
                    ]}
                    fileDate={song.fileDate}
                    songId={song.rid}
                    tracks={song.types}
                  />
                </TrackList>
              </div>
            </div>
          </div>
          {song.playlists.length ? (
            <Sidebar className={styles.relatedSongs}>
              <div className={styles.relatedHeader} id="related">
                <div className={styles.remixersOrRelatedButton}>
                  <TextStyle color="black" uppercase>
                    <FormattedMessage
                      defaultMessage="Playlists"
                      id="djcity.common.playlists"
                    />
                  </TextStyle>
                  {!isMobile &&
                    (carouselOverflow.left || carouselOverflow.right) && (
                      <CarouselNavControls
                        inactiveLeft={checkOverflow('left')}
                        inactiveRight={checkOverflow('right')}
                        onNext={() => handleClick('right')}
                        onPrev={() => handleClick('left')}
                      />
                    )}
                </div>
              </div>
              <Carousel
                items={song.playlists}
                ref={carouselRef}
                renderItem={playlist => (
                  <Link
                    className={styles.playlistItem}
                    key={playlist.slug}
                    to={`/playlists/${playlist.parentSlug}/#${playlist.slug}`}
                  >
                    <div
                      className={styles.playlistImageContainer}
                      id={playlist.slug}
                    >
                      <img
                        alt={playlist.name}
                        className={styles.playlistImage}
                        src={playlist.banner}
                      />
                      <Badge
                        createdOn={playlist.insertDate}
                        updatedOn={playlist.lastUpdatedDate}
                      />
                    </div>
                    <TextStyle
                      className={styles.playlistName}
                      color="grey"
                      tagName="p"
                      title={playlist.name}
                      truncate
                    >
                      {playlist.name}
                    </TextStyle>
                  </Link>
                )}
                setCarouselOverflow={(overflowLeft, overflowRight) => {
                  return setCarouselOverflow({
                    left: overflowLeft,
                    right: overflowRight,
                  })
                }}
              />
            </Sidebar>
          ) : null}

          {song.related.length ? (
            <Sidebar className={styles.relatedSongs}>
              <div className={styles.relatedHeader} id="related">
                <div className={styles.remixersOrRelatedButton}>
                  <div>
                    <TextStyle color="black" uppercase>
                      <FormattedMessage
                        defaultMessage="Remixes"
                        id="djcity.records.recordpool.track.remixes"
                      />
                    </TextStyle>
                    <sup>({song.related.length})</sup>
                  </div>
                </div>
              </div>
              <SimpleSongsList2
                addSongToQueue={addSongToQueue}
                queuedSongIds={queuedSongIds}
                removeSongFromQueue={removeSongFromQueue}
                showArtist={false}
                showPagination={true}
                showPopularity
                songs={song.related.map(t => songsById[t.rid])}
                trackList={TRACKLIST.RELATED}
              />
            </Sidebar>
          ) : null}
          <Sidebar className={styles.relatedSongs}>
            <div className={styles.relatedHeader} id="related">
              <div className={styles.remixersOrRelatedButton}>
                <TextStyle color="black" uppercase>
                  <FormattedMessage
                    defaultMessage="Related Tracks"
                    id="djcity.records.recordpool.track.related"
                  />
                </TextStyle>
                <sup> </sup>
              </div>
            </div>
            <SimpleSongsList2
              addSongToQueue={addSongToQueue}
              queuedSongIds={queuedSongIds}
              removeSongFromQueue={removeSongFromQueue}
              showArtist={false}
              showPopularity
              songs={song.relatedNew.map(t => songsById[t.rid])}
              trackList={TRACKLIST.RELATED_NEW_STACKED_STYLE}
            />
          </Sidebar>

          <Helmet defer={false}>
            <meta content="text/html; charset=utf-8" httpEquiv="Content-Type" />
            <title key="pagetitle">
              {song.title} - {song.artist} | DJcity
            </title>
            <meta
              content={intl.formatMessage(localizeMessages.metaDescription, {
                artist: song.artist,
                title: song.title,
              })}
              key="meta-description"
              name="description"
            />
            <meta
              content={`${song.artist} - ${song.title} | DJcity`}
              key="meta-og:title"
              property="og:title"
            />
            <meta content="song" key="meta-og:type" property="og:type" />
            <meta
              content={`https://www.djcity.com${location.pathname}`}
              key="meta-og:url"
              property="og:url"
            />
            <meta
              content="DJcity Record Pool"
              key="meta-og:site_name"
              property="og:site_name"
            />
            <meta
              content={`${song.artist} - ${song.title} | DJcity`}
              key="meta-twitter:title"
              name="twitter:title"
            />
          </Helmet>
        </>
      ) : (
        <>
          <div className={styles.songDetails}>
            <div className={styles.songDetailsFirstSection}>
              <div className={styles.firstRow}>
                <div className={styles.titleContainer}>
                  <PlayTrackButton
                    className={styles.playButton}
                    songId={song.rid}
                  />
                  <div>
                    <div className={styles.songTitle}>{song.title}</div>
                    <TextStyle
                      className={classNames('artist-names', styles.artistName)}
                      color="white"
                      uppercase
                      variant="extra-bold"
                    >
                      {linkifyArtistNames(song)}
                    </TextStyle>
                  </div>
                </div>
                {isLoggedIn && (
                  <PreviewQueueButton
                    className={styles.queueButton}
                    currentlyInQueue={currentlyInQueue}
                    onClick={onClickQueue}
                  />
                )}
              </div>
              <div className={styles.metadataLine}>
                {song.remixer ? (
                  <div className={styles.meta}>
                    <TextStyle color="white" variant="extra-bold">
                      <FormattedMessage
                        defaultMessage="Remixer"
                        id="djcity.records.recordpool.track.remixer"
                      />
                    </TextStyle>
                    <Link to={`/remixer/${song.remixer.slug}`}>
                      <TextStyle color="red" variant="label">
                        {song.remixer.name}
                      </TextStyle>
                    </Link>
                  </div>
                ) : null}
                <div className={styles.meta}>
                  <TextStyle color="white" variant="extra-bold">
                    {intl.formatMessage(localizeMessages.bpm)}
                  </TextStyle>
                  {song.bpm}
                </div>
                {song.key !== DEFAULT_KEY_INFORMATION ? (
                  <div className={styles.meta}>
                    <TextStyle color="white" variant="extra-bold">
                      {intl.formatMessage(localizeMessages.key)}
                    </TextStyle>
                    {song.key}
                  </div>
                ) : null}
                <div className={styles.meta}>
                  <TextStyle color="white" variant="extra-bold">
                    {intl.formatMessage(localizeMessages.genre)}
                  </TextStyle>
                  {song.category}
                </div>
                <div className={styles.meta}>
                  <TextStyle color="white" variant="extra-bold">
                    <FormattedMessage
                      defaultMessage="Release Date"
                      id="djcity.records.recordpool.track.release"
                    />
                  </TextStyle>
                  <span>
                    {intl.formatDate(
                      song.releasedate,
                      DEFAULT_SHORT_DATE_OPTION
                    )}
                  </span>
                </div>
                {song.recordLabel && song.recordLabel.labelName ? (
                  <div className={styles.meta}>
                    <div className={styles.additionalInfoBlock}>
                      <TextStyle color="white" variant="extra-bold">
                        <FormattedMessage
                          defaultMessage="Label"
                          id="djcity.records.recordpool.track.label"
                        />
                      </TextStyle>
                      <TextStyleMultiline
                        className={styles.additionalInfoBlockText}
                        color="white"
                        tagName="div"
                        variant="label"
                      >
                        {song.recordLabel.labelName}
                      </TextStyleMultiline>
                    </div>
                  </div>
                ) : null}
              </div>
            </div>
            <div className={styles.sectionSeparator} />
            <div className={styles.songDetailsSecondSection}>
              <div className={styles.tagsAndOtherInfo}>
                <div className={styles.tagList}>
                  {song.tags &&
                    song.tags.map(({ id, name, slug }) => {
                      return (
                        <Link
                          className={styles.tag}
                          key={id}
                          to={`/genre/${slug}`}
                        >
                          {name}
                        </Link>
                      )
                    })}
                </div>
                <div className={styles.otherInfo}>
                  {song.otherInfo ? (
                    <div className={styles.additionalInfoBlock}>
                      <TextStyle color="white" variant="extra-bold">
                        <FormattedMessage
                          defaultMessage="Other Info"
                          id="djcity.records.recordpool.track.description"
                        />
                      </TextStyle>
                      <TextStyleMultiline
                        className={styles.additionalInfoBlockText}
                        color="white"
                        tagName="div"
                        variant="label"
                      >
                        {song.otherInfo}
                      </TextStyleMultiline>
                    </div>
                  ) : null}
                  <div className={styles.moreInfoBlock}>
                    <TextStyle color="white" variant="extra-bold">
                      <FormattedMessage
                        defaultMessage="More"
                        id="djcity.records.recordpool.track.more"
                      />
                    </TextStyle>
                    <a
                      className={styles.webLink}
                      href={`${intl.formatMessage(
                        localizeMessages.beatsourceSearchLink
                      )}?${getQueryParams({ q: song.artist })}`}
                      rel="noopener noreferrer"
                      target="_blank"
                    >
                      {intl.formatMessage(localizeMessages.beatsourceLink, {
                        artist: song.artist,
                      })}
                    </a>
                  </div>
                </div>
              </div>
              <div className={styles.versions}>
                <div className={styles.versionBlock}>
                  <Popover
                    closeOnScroll={false}
                    contentClassName={styles.versionPopoverContent}
                    renderTrigger={({ handleClick, ref }) => (
                      <button
                        className={classNames(
                          'cleanButton',
                          styles.versionButton
                        )}
                        onClick={handleClick}
                        ref={ref}
                      >
                        <FontAwesomeIcon
                          className={styles.infoVersion}
                          icon="info-circle"
                        />
                        <TextStyle color="white" variant="extra-bold">
                          <FormattedMessage
                            defaultMessage="Versions"
                            id="djcity.records.recordpool.track.versions"
                          />
                        </TextStyle>
                      </button>
                    )}
                    variant="light"
                  >
                    <TrackDropDownTableInfo />
                  </Popover>
                </div>
                <TrackList className="darkMode">
                  {song.types.map(track => {
                    return (
                      <TrackListItem
                        bold
                        className={styles.trackListItemDetails}
                        fileDate={track.fileDate}
                        key={track.tid}
                        songId={song?.rid}
                        trackId={track.tid}
                      />
                    )
                  })}
                  <TrackListItemAll
                    bold
                    className={[
                      styles.trackListItemDetails,
                      styles.trackListItemDetailsAll,
                    ]}
                    fileDate={song.fileDate}
                    songId={song.rid}
                    tracks={song.types}
                  />
                </TrackList>
              </div>
            </div>
          </div>
          {song.playlists.length ? (
            <div className={styles.relatedSongs} id="related">
              <div className={styles.relatedHeader}>
                <div className={styles.remixersOrRelatedButton}>
                  <TextStyle color="black" uppercase>
                    <FormattedMessage
                      defaultMessage="Playlists"
                      id="djcity.common.playlists"
                    />
                  </TextStyle>
                  {!isMobile && (
                    <CarouselNavControls
                      inactiveLeft={checkOverflow('left')}
                      inactiveRight={checkOverflow('right')}
                      onNext={() => handleClick('right')}
                      onPrev={() => handleClick('left')}
                    />
                  )}
                </div>
              </div>
              <Carousel
                items={song.playlists}
                ref={carouselRef}
                renderItem={playlist => (
                  <Link
                    className={styles.playlistItem}
                    key={playlist.slug}
                    to={`/playlists/${playlist.parentSlug}/#${playlist.slug}`}
                  >
                    <div
                      className={styles.playlistImageContainer}
                      id={playlist.slug}
                    >
                      <img
                        alt={playlist.name}
                        className={styles.playlistImage}
                        src={playlist.banner}
                      />
                      <Badge
                        createdOn={playlist.insertDate}
                        updatedOn={playlist.lastUpdatedDate}
                      />
                    </div>
                    <TextStyle
                      className={styles.playlistName}
                      color="grey"
                      tagName="p"
                      title={playlist.name}
                      truncate
                    >
                      {playlist.name}
                    </TextStyle>
                  </Link>
                )}
                setCarouselOverflow={(overflowLeft, overflowRight) => {
                  return setCarouselOverflow({
                    left: overflowLeft,
                    right: overflowRight,
                  })
                }}
              />
            </div>
          ) : null}
          {song.related.length ? (
            <div className={styles.relatedSongs} id="related">
              <div className={styles.relatedHeader}>
                <div className={styles.remixersOrRelatedButton}>
                  <div>
                    <TextStyle color="black" uppercase>
                      <FormattedMessage
                        defaultMessage="Remixes"
                        id="djcity.records.recordpool.track.remixes"
                      />
                    </TextStyle>
                    <sup>({song.related.length})</sup>
                  </div>
                </div>
              </div>
              <SimpleSongsList2
                addSongToQueue={addSongToQueue}
                queuedSongIds={queuedSongIds}
                removeSongFromQueue={removeSongFromQueue}
                showArtist={false}
                showPagination={true}
                showPopularity
                songs={song.related.map(t => songsById[t.rid])}
                trackList={TRACKLIST.RELATED}
              />
            </div>
          ) : null}

          <div className={styles.relatedSongs} id="related">
            <div className={styles.relatedHeader}>
              <div className={styles.remixersOrRelatedButton}>
                <TextStyle color="black" uppercase>
                  <FormattedMessage
                    defaultMessage="Related Tracks"
                    id="djcity.records.recordpool.track.related"
                  />
                </TextStyle>
                <sup> </sup>
              </div>
            </div>
            <SimpleSongsList2
              addSongToQueue={addSongToQueue}
              queuedSongIds={queuedSongIds}
              removeSongFromQueue={removeSongFromQueue}
              showArtist={false}
              showPopularity
              songs={song.relatedNew.map(t => songsById[t.rid])}
              trackList={TRACKLIST.RELATED_NEW_STACKED_STYLE}
            />
          </div>

          <Helmet>
            <meta content="text/html; charset=utf-8" httpEquiv="Content-Type" />
            <title key="pagetitle">
              {song.title} - {song.artist} | DJcity
            </title>
            <meta
              content={intl.formatMessage(localizeMessages.metaDescription, {
                artist: song.artist,
                title: song.title,
              })}
              key="meta-description"
              name="description"
            />
            <meta
              content={`${song.artist} - ${song.title} | DJcity`}
              key="meta-og:title"
              property="og:title"
            />
            <meta content="song" key="meta-og:type" property="og:type" />
            <meta
              content={`https://www.djcity.com${location.pathname}`}
              key="meta-og:url"
              property="og:url"
            />
            <meta
              content="DJcity Record Pool"
              key="meta-og:site_name"
              property="og:site_name"
            />
            <meta
              content={`${song.artist} - ${song.title} | DJcity`}
              key="meta-twitter:title"
              name="twitter:title"
            />
          </Helmet>
        </>
      )}
    </div>
  )
}

SongDetails.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
    formatDate: PropTypes.func.isRequired,
  }).isRequired,
}

export default injectIntl(SongDetails)
